import React, { useState } from "react";
import SEOHead from "../../components/SEO/SEOHead";
import { generateWebPageStructuredData, generateBreadcrumbStructuredData } from "../../components/SEO/StructuredData";
import LegalPageLayout from "../../components/Legal/LegalPageLayout";

const DataRetentionPolicy = () => {
  const [activeSection, setActiveSection] = useState("purpose");

  const lastUpdated = "September 29, 2026";

  const structuredData = generateWebPageStructuredData({
    name: "Data Retention Policy",
    description: "Learn about our data retention policy, including retention periods for different data types, deletion procedures, and your rights regarding data retention.",
    url: "/data-retention-policy",
    dateModified: "2026-09-29",
  });

  const breadcrumbData = generateBreadcrumbStructuredData([
    { label: "Home", path: "/" },
    { label: "Data Retention Policy", path: "/data-retention-policy" },
  ]);

  const sections = [
    { id: "purpose", title: "Purpose" },
    { id: "scope", title: "Scope" },
    { id: "retention-periods", title: "Retention Periods" },
    { id: "anonymization", title: "Anonymization and Deletion" },
    { id: "active-inactive", title: "Active vs Inactive Accounts" },
    { id: "exceptions", title: "Exceptions" },
    { id: "review", title: "Review" },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <SEOHead
        title="Data Retention Policy - Bowling Tournament App"
        description="Learn about our data retention policy, including retention periods for different data types, deletion procedures, and your rights regarding data retention."
        keywords={[
          "data retention",
          "data deletion",
          "privacy policy",
          "GDPR",
          "data retention periods",
          "delete account",
          "data removal",
        ]}
        canonicalUrl="/data-retention-policy"
        noIndex={false}
        additionalMeta={[
          { name: "last-modified", content: lastUpdated },
          { name: "author", content: "Bowling Tournament App" },
        ]}
        structuredData={structuredData}
        additionalStructuredData={[breadcrumbData]}
      />
      <LegalPageLayout
        title="Data Retention Policy"
        lastUpdated={lastUpdated}
        sections={sections}
        activeSection={activeSection}
        onSectionClick={scrollToSection}
      >
        <div className="space-y-12">
                {/* Purpose Section */}
                <section id="purpose">
                  <h2 className="heading-display text-xl mb-4">
                    Purpose
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p>
                      This policy outlines how long personal and
                      tournament-related data is retained on the Bowling
                      Tournament App platform and how it is disposed of once the
                      retention period expires.
                    </p>
                  </div>
                </section>

                {/* Scope Section */}
                <section id="scope">
                  <h2 className="heading-display text-xl mb-4">
                    1. Scope
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p className="mb-4">
                      This policy applies to all personal data collected through
                      the platform, including:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Bowler registration data (name, email, phone, USBC ID,
                        averages)
                      </li>
                      <li>Scores and team history</li>
                      <li>Sidepot/payout records</li>
                      <li>
                        Any other tournament-related personal information
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Retention Periods Section */}
                <section id="retention-periods">
                  <h2 className="heading-display text-xl mb-4">
                    2. Retention Periods
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <div className="overflow-x-auto mb-6">
                      <table className="min-w-full divide-y divide-brand-charcoal/10">
                        <thead className="bg-brand-charcoal">
                          <tr>
                            <th className="px-4 py-3 text-left font-display font-bold text-brand-cream">
                              Data Type
                            </th>
                            <th className="px-4 py-3 text-left font-display font-bold text-brand-cream">
                              Retention Period
                            </th>
                            <th className="px-4 py-3 text-left font-display font-bold text-brand-cream">
                              Notes
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="px-4 py-3 text-brand-charcoal border-t border-brand-charcoal/10">
                              Bowler registration information
                            </td>
                            <td className="px-4 py-3 text-brand-charcoal border-t border-brand-charcoal/10">
                              7 years from last tournament participation or
                              account inactivity
                            </td>
                            <td className="px-4 py-3 text-brand-charcoal border-t border-brand-charcoal/10">
                              Active accounts may retain data longer for
                              operational purposes
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-brand-charcoal border-t border-brand-charcoal/10">
                              Scores, averages, team history
                            </td>
                            <td className="px-4 py-3 text-brand-charcoal border-t border-brand-charcoal/10">
                              10 years
                            </td>
                            <td className="px-4 py-3 text-brand-charcoal border-t border-brand-charcoal/10">
                              May be anonymized even if account is active
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-brand-charcoal border-t border-brand-charcoal/10">
                              Payment / fee records
                            </td>
                            <td className="px-4 py-3 text-brand-charcoal border-t border-brand-charcoal/10">
                              7 years
                            </td>
                            <td className="px-4 py-3 text-brand-charcoal border-t border-brand-charcoal/10">
                              Standard accounting requirement
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-brand-charcoal border-t border-brand-charcoal/10">
                              Sidepot / payout records
                            </td>
                            <td className="px-4 py-3 text-brand-charcoal border-t border-brand-charcoal/10">
                              7 years
                            </td>
                            <td className="px-4 py-3 text-brand-charcoal border-t border-brand-charcoal/10">
                              For dispute resolution or audit purposes
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        Personal data may be retained longer if required by law,
                        regulatory compliance, or pending disputes.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Anonymization and Deletion Section */}
                <section id="anonymization">
                  <h2 className="heading-display text-xl mb-4">
                    3. Anonymization and Deletion
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        After the retention period, personal identifiers (email,
                        phone, USBC ID) may be anonymized.
                      </li>
                      <li>
                        Aggregate data (scores, averages) may be retained
                        indefinitely for historical and statistical purposes.
                      </li>
                      <li>
                        Data deletion or anonymization events are logged for
                        audit purposes.
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Active vs Inactive Accounts Section */}
                <section id="active-inactive">
                  <h2 className="heading-display text-xl mb-4">
                    4. Active vs Inactive Accounts
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Active accounts:</strong> Data necessary for
                        platform functionality may be retained beyond the
                        standard retention period.
                      </li>
                      <li>
                        <strong>Inactive accounts:</strong> Retention periods
                        start from the last activity date or tournament
                        participation.
                      </li>
                      <li>
                        <strong>Admin-entered bowler data:</strong> While
                        tournament organizers act as Data Controllers for
                        manually entered data (as outlined in our{" "}
                        <a
                          href="/data-processing-agreement"
                          className="text-brand-gold-ink hover:text-brand-charcoal underline"
                        >
                          Data Processing Agreement
                        </a>
                        ), all data stored on the platform, regardless of how it
                        was entered, is subject to the retention periods and
                        procedures specified in this policy. Organizers must
                        comply with the platform's retention policy and cannot
                        dictate alternative retention periods.
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Exceptions Section */}
                <section id="exceptions">
                  <h2 className="heading-display text-xl mb-4">
                    5. Exceptions
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Legal holds or regulatory requirements may override
                        automatic deletion.
                      </li>
                      <li>
                        A deletion request removes the account and contact
                        fields that match it: login, email, phone, and profile.
                        It does not delete tournaments, scores, teams, payouts,
                        or public results. The bowler&apos;s name can still show
                        on Results.
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Review Section */}
                <section id="review">
                  <h2 className="heading-display text-xl mb-4">
                    6. Review
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p>
                      Retention periods are reviewed annually to ensure
                      compliance with privacy laws and platform needs.
                    </p>
                  </div>
                </section>

                {/* Contact & Updates Section */}
                <section className="border-t border-brand-charcoal/10 pt-8">
                  <h2 className="heading-display text-xl mb-4">
                    Contact Us & Policy Updates
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Questions About Data Retention?
                    </h3>
                    <p className="mb-4">
                      If you have questions about this Data Retention Policy or
                      wish to request data deletion, please contact us:
                    </p>
                    <p className="font-medium">
                      Email: privacy@bowlingtournamentapp.com
                    </p>
                    <p className="text-sm text-brand-muted">
                      We typically respond within 24-48 hours
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Policy Updates
                    </h3>
                    <p>
                      We may update this Data Retention Policy to reflect changes
                      in our practices or legal requirements.
                    </p>
                    <p className="font-medium mt-4">
                      Last Updated: {lastUpdated}
                    </p>
                    <p className="text-sm text-brand-muted">
                      Significant changes will be communicated via email to
                      registered users
                    </p>
                  </div>
                </section>
        </div>
      </LegalPageLayout>
    </>
  );
};

export default DataRetentionPolicy;
